import DOMPurify from "dompurify";
import { useMemo } from "react";

export function SafeHtml({ html, className = "" }: { html: string; className?: string }) {
  const cleanHtml = useMemo(() => {
    const sanitized = DOMPurify.sanitize(html, {
        FORBID_TAGS: ["script", "style", "form", "input", "button", "iframe", "object", "embed"],
        FORBID_ATTR: ["style", "onerror", "onload", "onclick", "srcdoc"],
        ADD_ATTR: ["target", "controls", "playsinline"],
      });
    const container = document.createElement("div");
    container.innerHTML = sanitized;

    for (const link of container.querySelectorAll<HTMLAnchorElement>("a[href]")) {
      const href = link.getAttribute("href")?.trim() || "";
      if (href === "#") {
        const replacement = document.createElement("span");
        replacement.className = "moodle-link-unavailable";
        replacement.innerHTML = link.innerHTML;
        link.replaceWith(replacement);
      } else if (/^https?:\/\//i.test(href)) {
        link.target = "_blank";
        link.rel = "noopener noreferrer";
      } else {
        link.removeAttribute("target");
        link.removeAttribute("rel");
      }
    }

    for (const media of container.querySelectorAll<HTMLMediaElement>("video, audio")) {
      const directSource = media.getAttribute("src")?.trim();
      const nestedSource = [...media.querySelectorAll<HTMLSourceElement>("source[src]")]
        .some((source) => Boolean(source.getAttribute("src")?.trim()));
      const hasPlayableSource = Boolean(directSource || nestedSource);
      const fallback = media.querySelector<HTMLAnchorElement>("a[href]");
      if (!hasPlayableSource && fallback) {
        const replacement = document.createElement("a");
        replacement.href = fallback.getAttribute("href") || "";
        replacement.target = "_blank";
        replacement.rel = "noopener noreferrer";
        replacement.className = "embedded-video-link";
        replacement.textContent = "Watch the supporting video";
        media.replaceWith(replacement);
      } else if (!hasPlayableSource) {
        const replacement = document.createElement("p");
        replacement.className = "media-unavailable";
        replacement.textContent = media.tagName === "AUDIO"
          ? "The source audio was not included in the Moodle export."
          : "The source video was not included in the Moodle export.";
        media.replaceWith(replacement);
      }
    }

    for (const image of container.querySelectorAll<HTMLImageElement>('img[src=""]')) {
      image.remove();
    }
    return container.innerHTML;
  }, [html]);

  return <div className={className} dangerouslySetInnerHTML={{ __html: cleanHtml }} />;
}
